import type { AdvisedProduct, Integration } from "../../../app/types"
import type { Product } from "../types"
import { StandardUsageEvents } from "./implementations/Standard"
import { SqueezelyUsageEvents } from "./implementations/Squeezely"
import type {
  AdvisorInfo,
  SessionContext,
  UsageAnalyticsEventTransformer,
  UsageEvent,
} from "./types"
import { SessionActivityEvents } from "./implementations/SessionActivity"
import { BloomReachUsageEvents } from "./implementations/BloomReach"
import { GoogleTagManagerUsageEvents } from "./implementations/GoogleTagManager"
import { GoogleAnalyticsUsageEvents } from "./implementations/GoogleAnalytics"
import {SpotlerActivateUsageEvents} from "./implementations/SpotlerActivate";

/**
Usage Events are things that happen when using the Aiden app.
These things that happen have to be converted into events for each configured integration.

Currently we support integrations for:
- Spotler activate (formerly Squeezely)
- BloomReach
- Google Analytics / Google Tag manager

*/
export const UsageEvents = {
  questionAnswered: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    question: { title: string; label: string },
    answers: string[]
  ): UsageEvent[] => {
    return implFor(integrations).flatMap((impl) =>
      impl.questionAnswered(advisor, context, integrations, question, answers)
    )
  },

  adviceGiven: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    products: Product[]
  ): UsageEvent[] => {
    return implFor(integrations).flatMap((impl) =>
      impl.adviceGiven(advisor, context, integrations, products)
    )
  },

  startOver: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[]
  ): UsageEvent[] => {
    return [
      ...StandardUsageEvents.startOver(advisor, context, integrations),
      ...SessionActivityEvents.startOver(advisor, context)
    ]
  },

  clickedOnProduct: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    product: Product
  ): UsageEvent[] => {
    return implFor(integrations).flatMap((impl) =>
      impl.clickedOnProduct(advisor, context, integrations, product)
    )
  },

  customerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
      advisedProducts: AdvisedProduct[]
    }
  ): UsageEvent[] => {
    return implFor(integrations).flatMap((impl) =>
      impl.customerInfoSubmitted(
        advisor,
        context,
        integrations,
        isPreview,
        info
      )
    )
  },

  infoPageCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
      formIdentifier: string
    }
  ): UsageEvent[] => {
    return implFor(integrations).flatMap((impl) =>
      impl.infoPageCustomerInfoSubmitted(
        advisor,
        context,
        integrations,
        isPreview,
        info
      )
    )
  },

  noResultsCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
    }
  ): UsageEvent[] => {
    return implFor(integrations).flatMap((impl) =>
      impl.noResultsCustomerInfoSubmitted(
        advisor,
        context,
        integrations,
        isPreview,
        info
      )
    )
  },

  conversationStarted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[]
  ): UsageEvent[] => {
    return implFor(integrations).flatMap((impl) =>
      impl.conversationStarted(advisor, context, integrations)
    )
  },
}

function implFor(
  integrations: Integration[]
): UsageAnalyticsEventTransformer[] {
  return [
    ...standardEventIntegrations(),
    ...integrationSpecificIntegration(integrations),
  ]
}

function integrationSpecificIntegration(
  integrations: Integration[]
): UsageAnalyticsEventTransformer[] {
  return integrations
    .map((integration) => {
      switch (integration.name) {
        case "standard": {
          return StandardUsageEvents
        }
        case "squeezely": {
          return SqueezelyUsageEvents(integration.config)
        }
        case "spotler_activate": {
          return SpotlerActivateUsageEvents(integration.config)
        }
        case "bloomreach": {
          return BloomReachUsageEvents
        }
        case "google_tag_manager": {
          return GoogleTagManagerUsageEvents
        }
        case "google_analytics": {
          return GoogleAnalyticsUsageEvents
        }
        default: {
          // Used for exhaustiveness checking, 'cause TypeScript
          assertUnreachable(integration)
        }
      }
    })
    .filter((a): a is UsageAnalyticsEventTransformer => Boolean(a))
}

function standardEventIntegrations(): UsageAnalyticsEventTransformer[] {
  return [SessionActivityEvents]
}

// eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
function assertUnreachable(_x: never): never {
  throw new Error("Didn't expect to get here")
}
